import React, {
	memo,
	useCallback,
	type ComponentType,
	type MouseEvent,
	type KeyboardEvent,
} from 'react';
import { styled } from '@compiled/react';
import usePressTracing from '@atlaskit/react-ufo/use-press-tracing';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { openViewFromEmbed } from '@atlassian/jira-business-common/src/common/utils/embeds/index.tsx';
import { SELECTED_ISSUE_PARAM } from '@atlassian/jira-business-constants/src/index.tsx';
import { useSimpleSearchHighlight } from '@atlassian/jira-business-filters/src/controllers/simple-search/index.tsx';
import { usePrefetchIssueViewDataActions } from '@atlassian/jira-business-issue-view-critical-data-loader/src/controllers/use-prefetch-issue-view-data/index.tsx';
import { OPTIMISTIC_ID_PREFIX } from '@atlassian/jira-business-list-handlers/src/common/constants.tsx';
import { isModifiedEvent } from '@atlassian/jira-business-modified-click-event/src/index.tsx';
import { borderRadius } from '@atlassian/jira-common-styles/src/main.tsx';
import { styledComponentWithCondition } from '@atlassian/jira-compiled-migration/src/ui/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useNudgeStep } from '@atlassian/jira-onboarding-quickstart-core/src/services/index.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import HighlightText from '@atlassian/jira-software-highlight-text/src/ui/index.tsx';
import { TimelineAddChildItemNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/jwm-timeline-nudge-tour/timeline-add-child-item/async.tsx';
import { INSIDE } from '@atlassian/timeline-table/common/constants';
import type { OnCreateClick } from '@atlassian/timeline-table/common/types/callbacks';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { useCreateItemAnchor } from '../../../controllers/create-item-anchor/index.tsx';
import { useTimelineFeatures } from '../../../controllers/features-context/index.tsx';
import type { TimelineViewIssue } from '../../../services/get-timeline-view-issues/types.tsx';
import Icon from './icon/index.tsx';
import messages from './messages.tsx';

// Replace with lodash/noop
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

type ListItemContentProps = {
	isFirstItem: boolean;
	issue?: TimelineViewIssue;
	title?: string;
	setSelectedIssueKey?: (arg1: string) => void;
	onCreateClick: OnCreateClick;
	isLastItem: boolean;
	setInfiniteScrollerRef: (node: Element | null) => void;
};
type ListItemContentNudgesProps = {
	onChildCreateClick: () => void;
};

export const ListItemContentNudges: ComponentType<ListItemContentNudgesProps> = memo(
	({ onChildCreateClick }: ListItemContentNudgesProps) => {
		const [nudgeStep] = useNudgeStep();
		const [{ createItemAnchor }] = useCreateItemAnchor();

		const shouldShowAddChildItemNudge =
			nudgeStep === 'timelineAddChildItemNudge' && !createItemAnchor;

		return (
			<>
				{shouldShowAddChildItemNudge && (
					<AddItemNudgeWrapper>
						<TimelineAddChildItemNudgeAsync>
							<FakeChildCreateButton onClick={onChildCreateClick} />
						</TimelineAddChildItemNudgeAsync>
					</AddItemNudgeWrapper>
				)}
			</>
		);
	},
);

export const IssueTitle = (props: { title?: string }) => {
	const highlight = useSimpleSearchHighlight();
	const { title } = props;

	if (title == null) {
		return null;
	}

	const shouldHighlight = highlight.length > 0;

	return (
		<IssueTitleWrapper>
			{shouldHighlight ? <HighlightText highlight={highlight} text={title} /> : title}
		</IssueTitleWrapper>
	);
};

export const ListItemContent: ComponentType<ListItemContentProps> = memo(
	({
		title,
		setSelectedIssueKey = noop,
		issue,
		isFirstItem,
		onCreateClick,
		isLastItem,
		setInfiniteScrollerRef,
	}: ListItemContentProps) => {
		const ufoPressTracing = usePressTracing('jwm.timeline.issue-view-opened');

		const { formatMessage } = useIntl();

		const { prefetchIssue } = usePrefetchIssueViewDataActions();

		const handleIssueKeyMouseEnter = useCallback(() => {
			if (issue?.key) {
				prefetchIssue(issue?.key);
			}
		}, [issue?.key, prefetchIssue]);

		const { issueViewInteraction } = useTimelineFeatures();
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const openIssueModal = useCallback(
			(event: MouseEvent | KeyboardEvent) => {
				if (issue && issue.id && !issue.id.startsWith(OPTIMISTIC_ID_PREFIX) && issue.key) {
					ufoPressTracing(event.timeStamp);
					if (issueViewInteraction === 'openNewTab') {
						fireUIAnalytics(createAnalyticsEvent({}), 'timelineEmbedIssueKey clicked');
						openViewFromEmbed({ key: SELECTED_ISSUE_PARAM, value: issue.key });
						event.preventDefault();
						event.stopPropagation();
					} else if (!isModifiedEvent(event)) {
						setSelectedIssueKey(issue.key);
						event.preventDefault();
						event.stopPropagation();
						fireUIAnalytics(createAnalyticsEvent({}), 'timelineIssueKey clicked');
					}
				}
			},
			[issue, issueViewInteraction, createAnalyticsEvent, setSelectedIssueKey, ufoPressTracing],
		);

		const onChildCreate = useCallback(() => {
			if (issue) {
				onCreateClick({ parentId: issue.issueId, position: INSIDE });
			}
		}, [onCreateClick, issue]);

		if (!issue) {
			return null;
		}

		return (
			<>
				<ListItemContentWrapper>
					<Icon src={issue.issueType.iconUrl} alt={issue.issueType.name} />
					<Tooltip content={formatMessage(messages.toolTip)}>
						<IssueKeyLink
							onClick={openIssueModal}
							onMouseEnter={handleIssueKeyMouseEnter}
							href={`/browse/${issue.key}`}
							data-test-id="business-timeline.ui.timeline-view.list-item-content.issue-key"
							data-testid="business-timeline.ui.timeline-view.list-item-content.issue-key"
							isVisualRefresh={isVisualRefreshEnabled()}
						>
							{issue.key}
						</IssueKeyLink>
					</Tooltip>
					<IssueTitle title={title} />
				</ListItemContentWrapper>
				{isFirstItem && <ListItemContentNudges onChildCreateClick={onChildCreate} />}
				{isLastItem && <span ref={setInfiniteScrollerRef} />}
			</>
		);
	},
);

ListItemContent.displayName = 'ListItemContent';

const NUDGE_SIZE = 24;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AddItemNudgeWrapper = styled.div({
	position: 'absolute',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	inset: `${borderRadius}px`,
	pointerEvents: 'none',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-html-anchor -- To migrate as part of go/ui-styling-standard
const IssueKeyLink = styled.a<{ isVisualRefresh: boolean }>({
	fontWeight: token('font.weight.medium'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: ({ isVisualRefresh }) =>
		isVisualRefresh
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				token('color.text.subtlest', colors.N700)
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				token('color.text.subtle', colors.N90),
	whiteSpace: 'nowrap',
	cursor: 'pointer',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	marginRight: ({ isVisualRefresh }) =>
		isVisualRefresh ? token('space.050', '4px') : token('space.200', '16px'),
	marginLeft: token('space.100', '8px'),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div': {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		verticalAlign: 'middle',
	},
	'&:hover': {
		textDecoration: 'underline',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: token('color.text.subtle', colors.N90),
	},
	'&:focus': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: token('color.text.subtle', colors.N90),
		boxShadow: 'none',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueTitleWrapper = styled.span({
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ListItemContentWrapperNew = styled.div({
	display: 'flex',
	flex: 1,
	overflow: 'hidden',
	alignItems: 'center',
	paddingBlock: token('space.050'), // Prevents top and bottom of focus ring from being clipped
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ListItemContentWrapperOld = styled.div({
	display: 'flex',
	flex: 1,
	overflow: 'hidden',
	alignItems: 'center',
});

const ListItemContentWrapper = styledComponentWithCondition(
	() => isVisualRefreshEnabled(),
	ListItemContentWrapperNew,
	ListItemContentWrapperOld,
);

// All NudgeTooltipPulse's child nodes will be relative positioned, we have to override position value for the button
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const FakeChildCreateButton = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	position: 'absolute !important',

	top: `calc(50% - ${token('space.150', '12px')})`,
	right: token('space.075', '6px'),
	width: `${NUDGE_SIZE}px`,
	height: `${NUDGE_SIZE}px`,
	pointerEvents: 'auto',
	cursor: 'pointer',
	'&::before, &::after': {
		display: 'none',
	},
});
